import { Box, Button, Stack, Typography } from '@mui/material';

import RecordSection from './RecordSection';
import { TagChip } from './GuestTagChips';
import { GUEST_TEXT_BUTTON_SX } from './guestTableStyles';
import {
  AUTO_TAG_KIND,
  HEALTH_TAG_KINDS,
  asTagKind,
  sortTags,
  type CreateTagKind,
} from '../../../lib/guestTags';
import type { GuestTag, TrackerGuest } from '../../../services/tracker-api-client';

/**
 * A guest's tags, split into the two tiles they belong in.
 *
 * The split is not cosmetic. Dietary and access tags are health information:
 * they live behind the host gate, they never enter a public payload, a share
 * card or the concierge's group-context blob, and the tile says so in the same
 * words the room-and-confirmation-number tile already uses. Putting them in the
 * same well as "college friends" is how one of them ends up somewhere it should
 * not be.
 *
 * Removing a tag from here is the same batch call as removing it from two
 * hundred people — a batch of one. There is deliberately no second write path.
 */
export default function GuestTagsTile({
  guest,
  vocabulary,
  busy,
  onOpenPalette,
  onRemove,
}: {
  guest: TrackerGuest;
  vocabulary: GuestTag[];
  busy: boolean;
  /**
   * Opens the palette carrying the kind the BUTTON promised. Both buttons
   * opened it identically before, so "Record a requirement" typed into an empty
   * vocabulary produced a social tag that landed in the well above — the button
   * promised a health record and made a label.
   */
  onOpenPalette: (intent: CreateTagKind) => void;
  onRemove: (tagId: string) => void;
}) {
  const byId = new Map(vocabulary.map((tag) => [tag.id, tag]));
  const held = sortTags(
    (guest.tagIds ?? []).map((id) => byId.get(id)).filter((tag): tag is GuestTag => Boolean(tag)),
  );
  const health = held.filter((tag) => HEALTH_TAG_KINDS.includes(asTagKind(tag.kind)));
  const social = held.filter((tag) => !HEALTH_TAG_KINDS.includes(asTagKind(tag.kind)));

  const chips = (tags: GuestTag[], empty: string) =>
    tags.length === 0 ? (
      <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12 }}>{empty}</Typography>
    ) : (
      <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 0.75 }}>
        {tags.map((tag) => (
          <TagChip
            key={tag.id}
            tag={tag}
            note={guest.tagNotes?.[tag.id]}
            onDelete={busy ? undefined : () => onRemove(tag.id)}
          />
        ))}
      </Stack>
    );

  return (
    <>
      <RecordSection title="Tags" description="Groups, households, anything you sort people by.">
        <Stack spacing={1.5}>
          {chips(social, 'No tags yet.')}
          <Box>
            <Button
              variant="text"
              disabled={busy}
              onClick={() => onOpenPalette(AUTO_TAG_KIND)}
              sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-brand-ink)' }}
            >
              ＋ Add a tag
            </Button>
          </Box>
        </Stack>
      </RecordSection>

      <RecordSection
        title="Health & access"
        description="Allergies, diets, mobility. Host only — never shown to guests, never in a share card, never sent to the concierge."
      >
        <Stack spacing={1.5}>
          {chips(health, 'Nothing recorded.')}
          {health.some((tag) => tag.isCritical) ? (
            <Typography sx={{ color: 'var(--tsb-danger-ink)', fontSize: 11.5, lineHeight: 1.5 }}>
              Outlined tags are the ones that go on the kitchen sheet. Hover one to read its note.
            </Typography>
          ) : null}
          <Box>
            <Button
              variant="text"
              disabled={busy}
              /*
                Auto, not 'dietary'. Forcing a kind here sends one explicitly,
                which overrules the server's seeded vocabulary — so "Wheelchair",
                which the seed knows as an ACCESS tag, would be created as a
                dietary one: right tile, wrong kind, and it would then be counted
                on a catering sheet. Auto lets a seeded slug land as what it is
                and anything unknown fall to a social tag, which the host can
                retype in one dropdown.
              */
              onClick={() => onOpenPalette(AUTO_TAG_KIND)}
              sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-brand-ink)' }}
            >
              ＋ Record a requirement
            </Button>
          </Box>
        </Stack>
      </RecordSection>
    </>
  );
}
